
import { useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useErrorHandler } from '@/hooks/useErrorHandler';
import { cleanupAuthState } from '@/utils/authUtils';
import { logger } from '@/utils/logger';
import { Analytics } from '@/services/analytics';

export const useAuthOperations = () => {
  const { handleError } = useErrorHandler();

  const signUp = useCallback(async (email: string, password: string, metadata?: Record<string, unknown>) => {
    try {
      logger.debug('🚀 Tentative d\'inscription');
      
      // Nettoyer l'état avant l'inscription
      cleanupAuthState();
      
      // Détecter si on est sur mobile (Capacitor) ou web
      const isMobile = typeof window !== 'undefined' && (
        window.location.protocol === 'capacitor:' ||
        navigator.userAgent.includes('CapacitorWebView')
      );

      // Configuration de l'URL de redirection selon la plateforme
      const redirectTo = isMobile
        ? 'octoveto://auth/callback'
        : `${window.location.origin}/auth/callback`;

      logger.debug('📧 URL de redirection configurée:', redirectTo, 'Mobile:', isMobile);
      
      const { data, error } = await supabase.auth.signUp({
        email,
        password,
        options: {
          data: metadata,
          emailRedirectTo: redirectTo
        }
      });
      
      logger.debug('📤 Réponse inscription Supabase:', { success: !error });
      
      if (error) {
        logger.error('❌ Erreur inscription:', error.message);
        return { error };
      }
      
      // Inscription réussie
      if (data.user) {
        logger.debug('✅ Inscription réussie');
        
        // Vérifier si l'utilisateur doit confirmer son email
        if (!data.user.email_confirmed_at) {
          logger.debug('📧 Email de confirmation envoyé');
        } else {
          logger.debug('🎉 Compte créé et confirmé automatiquement');
        }
      }
      
      return { error: null };
    } catch (error) {
      logger.error('💥 Exception lors de l\'inscription:', error);
      handleError(error as Error, 'signup');
      return { error };
    }
  }, [handleError]);

  const signIn = useCallback(async (email: string, password: string) => {
    try {
      const { error } = await supabase.auth.signInWithPassword({
        email,
        password,
      });
      
      // Améliorer la gestion d'erreur pour distinguer les cas
      if (error) {
        logger.error('❌ Erreur de connexion:', error.message);

        // Uniquement si l'email n'est réellement pas confirmé
        if (error.message?.includes('email_not_confirmed') ||
            error.message?.includes('Email not confirmed') ||
            error.code === 'email_not_confirmed') {
          return {
            error: {
              message: 'Votre email n\'est pas encore confirmé. Vérifiez votre boîte mail ou cliquez sur "Renvoyer l\'email de confirmation".',
              code: 'email_not_confirmed',
              status: error.status || 400
            }
          };
        }
      }
      
      return { error };
    } catch (error) {
      logger.error('Erreur lors de la connexion:', error);
      handleError(error as Error, 'signin');
      return { error };
    }
  }, [handleError]);

  const signOut = useCallback(async () => {
    try {
      // Nettoyer l'état d'abord
      cleanupAuthState();
      
      // Tentative de déconnexion
      const { error } = await supabase.auth.signOut({ scope: 'global' });
      if (error) {
        logger.error('Erreur lors de la déconnexion:', error);
        handleError(error, 'signout');
      }
      
      logger.debug('✅ Déconnexion réussie');
      Analytics.logoutCompleted();

    } catch (error) {
      logger.error('Erreur lors de la déconnexion:', error);
      handleError(error as Error, 'signout');
    }
  }, [handleError]);

  const resetPassword = useCallback(async (email: string) => {
    try {
      const { error } = await supabase.auth.resetPasswordForEmail(email, {
        redirectTo: `${window.location.origin}/reset-password`,
      });
      
      return { error };
    } catch (error) {
      logger.error('Erreur lors de la réinitialisation:', error);
      handleError(error as Error, 'password reset');
      return { error };
    }
  }, [handleError]);

  const signInWithMagicLink = useCallback(async (email: string, metadata?: Record<string, unknown>) => {
    try {
      const isMobile = typeof window !== 'undefined' && (
        window.location.protocol === 'capacitor:' ||
        navigator.userAgent.includes('CapacitorWebView')
      );
      const redirectTo = isMobile
        ? 'octoveto://auth/callback'
        : `${window.location.origin}/auth/callback`;

      const { error } = await supabase.auth.signInWithOtp({
        email,
        options: {
          emailRedirectTo: redirectTo,
          data: metadata,
          shouldCreateUser: true,
        }
      });

      return { error };
    } catch (error) {
      logger.error('Erreur magic link:', error);
      handleError(error as Error, 'magic link');
      return { error };
    }
  }, [handleError]);

  const signInWithGoogle = useCallback(async () => {
    try {
      logger.debug('🔐 Tentative de connexion Google OAuth...');

      // Détecter si on est sur mobile (Capacitor) ou web
      const isMobile = typeof window !== 'undefined' && (
        window.location.protocol === 'capacitor:' ||
        navigator.userAgent.includes('CapacitorWebView')
      );

      // Configuration de l'URL de redirection selon la plateforme
      const redirectTo = isMobile
        ? 'octoveto://auth/callback'
        : `${window.location.origin}/auth/callback`;

      logger.debug('🌐 Redirect URL Google OAuth:', redirectTo, 'Mobile:', isMobile);
      
      const { data, error } = await supabase.auth.signInWithOAuth({
        provider: 'google',
        options: {
          redirectTo: redirectTo,
          queryParams: {
            access_type: 'offline',
            prompt: 'consent'
          }
        }
      });
      
      if (error) {
        logger.error('❌ Erreur Google OAuth:', error.message);
      } else if (data?.url) {
        logger.debug('✅ Redirection Google OAuth initiée');
      }
      
      return { error };
    } catch (error) {
      logger.error('💥 Exception lors de la connexion Google:', error);
      handleError(error as Error, 'google signin');
      return { error };
    }
  }, [handleError]);

  return {
    signUp,
    signIn,
    signOut,
    resetPassword,
    signInWithGoogle,
    signInWithMagicLink,
  };
};
